import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { IS_DEV, isRunningInNativeApp, native } from '@muzly/shared';

import { SETUP_ROUTE, isSetupPath, setupReturnState } from '../../../core';
import { toRoutePath } from '../utils';

/**
 * 네이티브 셸이 넘긴 딥링크(`deepLink` 알림)를 웹 라우트로 옮긴다. 브라우저에서는 no-op.
 *
 * 콜드 스타트 유실은 두 겹이 받는다 — 셸은 WebView 로드 전 링크를 모아 두고, 브리지는
 * 리스너가 붙기 전 알림을 모아 뒀다가 첫 구독 때 흘린다(`native.on`). 그래서 여기엔 큐가 없다.
 *
 * 초기 설정 중에 온 링크는 그리로 가지 않는다 — 가면 게이트가 `/setup` 으로 되돌리며 설문을
 * 새로 연다. 대신 설정이 끝난 뒤 갈 곳만 바꿔 둔다.
 *
 * 구독은 마운트 때 한 번이다. 지금 경로와 `navigate` 는 ref 로 읽는다 — 둘 다 이동마다
 * 바뀌므로 effect 의존성에 두면 이동마다 구독을 끊었다 다시 붙인다.
 */
export const useDeepLink = (): void => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const navigateRef = useRef(navigate);
  const pathnameRef = useRef(pathname);

  useEffect(() => {
    navigateRef.current = navigate;
    pathnameRef.current = pathname;
  }, [navigate, pathname]);

  useEffect(() => {
    if (!isRunningInNativeApp()) {
      return;
    }
    return native.on('deepLink', ({ url }) => {
      const path = toRoutePath(url);
      if (!path) {
        if (IS_DEV) {
          console.warn('[deepLink] ignored', url);
        }
        return;
      }
      if (isSetupPath(pathnameRef.current)) {
        navigateRef.current(SETUP_ROUTE, {
          replace: true,
          state: setupReturnState(path),
        });
        return;
      }
      navigateRef.current(path);
    });
  }, []);
};
